/**
 * Tedarikçi kartında şirket profili ve banka hesapları (ADR 0114 §6). Anahtarlar
 * `supplierProfile.*`.
 */
export const supplierProfileEn = {
  open: 'Company profile & bank',
  openOf: 'Open the company profile of {{name}}',
  title: '{{name}} · Company profile',
  close: 'Close',
  loading: 'Loading the company profile',
  tabs: { profile: 'Company profile', bank: 'Bank accounts' },
  verified: 'Verified',
  verifiedHint: 'Company documents checked by the Felixmate platform.',
  suspended: 'Suspended by platform',
  reviews: {
    tab: 'Reviews',
    summary: '{{average}} / 5 · {{count}} reviews',
    summaryNone: 'No published reviews yet',
    hint: 'Reviews from ship managers after a received order, published by the platform. The writing company is not shown.',
    verifiedOrder: 'Verified order',
    yourCompany: 'Your company',
    rating: '{{rating}} of 5',
    mineTitle: 'Reviews by your company',
    status: {
      pending: 'Awaiting approval',
      approved: 'Published',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn',
    },
    reason: 'Reason: {{reason}}',
    write: 'Write a review',
    writeTitle: 'Review {{name}}',
    writeBody:
      'Only after a received order. The platform approves the review before it is published; other ship managers do not see your company name.',
    order: 'Received order',
    rewrite: '{{po}} (rewrite after rejection)',
    ratingLabel: 'Rating',
    comment: 'Comment',
    commentHint: '10 to 2000 characters. Facts about this order; no personal data.',
    submit: 'Submit for approval',
    submitted: 'Review submitted for approval.',
    noOrders: 'No received order to review.',
    empty: 'No published reviews yet.',
  },
  reviewErrors: {
    INVALID_RATING: 'Choose a rating from 1 to 5.',
    TOO_SHORT: 'Write at least 10 characters.',
    ORDER_NOT_RECEIVED: 'Only a received order can be reviewed.',
    REVIEW_EXISTS: 'This order already has a review.',
    SUPPLIER_NOT_ON_PORTAL: 'This supplier is not on the Supplier Portal.',
    INVALID_REVIEW: 'Check the rating and the comment.',
  },
  readOnly: 'Entered by the supplier in the Supplier Portal; read-only here.',
  updated: 'Last updated by the supplier {{date}}',
  noPortal: 'This supplier is not linked to the Supplier Portal.',
  noDetails: 'The supplier has not entered company details yet.',
  legal: {
    title: 'Legal & tax',
    legalName: 'Legal name',
    taxId: 'Tax ID',
    taxOffice: 'Tax office',
    taxCountry: 'Tax country',
    account: 'Portal account',
  },
  billing: 'Billing address',
  shipping: 'Shipping address',
  sameAsBilling: 'Same as the billing address',
  contacts: {
    title: 'Contacts',
    name: 'Name',
    role: 'Role',
    email: 'E-mail',
    phone: 'Phone',
  },
  bank: {
    prospect: 'Bank accounts are shown after the supplier is added to your suppliers.',
    empty: 'The supplier has not entered a bank account yet.',
    default: 'Default',
    holder: 'Account holder',
    swift: 'SWIFT / BIC',
    show: 'Show full number',
    hide: 'Hide',
    audited: 'Viewing the full number is recorded in the audit log.',
    effective: 'In use for payments: {{bank}} {{number}}',
    noneEffective: 'No approved account yet: do not pay to this account.',
    sameEffective: 'In use for payments.',
    status: {
      pending: 'Pending verification',
      verified: 'Verified · awaiting approval',
      approved: 'Approved',
      rejected: 'Rejected',
      removed: 'Removed by the supplier',
    },
    verify: 'Record verification call',
    approve: 'Approve',
    reject: 'Reject',
    awaitingOther: 'You verified this change; a different colleague approves it.',
    history: 'History',
    change: {
      added: 'Added by the supplier (version {{n}})',
      changed: 'Changed by the supplier (version {{n}})',
      default: 'Default changed by the supplier (version {{n}})',
      removed: 'Removed by the supplier (version {{n}})',
    },
    review: {
      verified: 'Verified by {{name}}',
      approved: 'Approved by {{name}}',
      rejected: 'Rejected by {{name}}',
    },
  },
  dialogs: {
    verifyTitle: 'Record verification call',
    verifyBody:
      'Call the supplier on a phone number you already hold (not one given with this change), confirm the account holder, bank and full number, then record who you spoke to. A different colleague approves afterwards.',
    verifyNote: 'Call record (number called, person, what was confirmed)',
    verifyConfirm: 'Record verification',
    approveTitle: 'Approve the bank account?',
    approveBody:
      'Approving makes this account the one used for payments to the supplier in your company. You are the second person after the verification call.',
    approveConfirm: 'Approve bank account',
    rejectTitle: 'Reject the bank account?',
    rejectBody:
      'The previously approved account (if any) stays in use. The supplier sees the account as rejected and must enter it again.',
    rejectNote: 'Reason',
    rejectConfirm: 'Reject bank account',
  },
  notices: {
    verified: 'Verification recorded. A different colleague approves it.',
    approved: 'Bank account approved.',
    rejected: 'Bank account rejected.',
  },
  bell: {
    title: 'Bank account changes',
    item: 'Bank details changed',
    awaitingOther: 'Awaiting another approver',
  },
  errors: {
    NOTE_REQUIRED: 'Write the call record (at least 10 characters) or the reason.',
    NOTE_TOO_LONG: 'The note is too long (up to 1000 characters).',
    SAME_APPROVER_FORBIDDEN:
      'You recorded the verification call. A different colleague approves this account.',
    STALE_VERSION: 'The supplier changed this account meanwhile. Reopen the profile.',
    CONTENT_CHANGED: 'The supplier changed this account meanwhile. Reopen the profile.',
    INVALID_TRANSITION: 'This account was already reviewed. Reopen the profile.',
    FORBIDDEN: 'Approving bank accounts needs the purchasing approval permission.',
  },
};

export const supplierProfileTr: typeof supplierProfileEn = {
  open: 'Şirket profili ve banka',
  openOf: '{{name}} şirket profilini aç',
  title: '{{name}} · Şirket profili',
  close: 'Kapat',
  loading: 'Şirket profili yükleniyor',
  tabs: { profile: 'Şirket profili', bank: 'Banka hesapları' },
  verified: 'Doğrulanmış',
  verifiedHint: 'Şirket belgeleri Felixmate platformunca kontrol edildi.',
  suspended: 'Platformca askıda',
  reviews: {
    tab: 'Yorumlar',
    summary: '{{average}} / 5 · {{count}} yorum',
    summaryNone: 'Henüz yayımlanmış yorum yok',
    hint: 'Armatörlerin teslim alınmış siparişten sonra yazdığı, platformun yayımladığı yorumlar. Yazan şirket gösterilmez.',
    verifiedOrder: 'Doğrulanmış sipariş',
    yourCompany: 'Şirketiniz',
    rating: '5 üzerinden {{rating}}',
    mineTitle: 'Şirketinizin yorumları',
    status: {
      pending: 'Onay bekliyor',
      approved: 'Yayında',
      rejected: 'Reddedildi',
      withdrawn: 'Kaldırıldı',
    },
    reason: 'Gerekçe: {{reason}}',
    write: 'Yorum yaz',
    writeTitle: '{{name}} için yorum',
    writeBody:
      'Yalnız teslim alınmış siparişten sonra. Yorum platform onayından sonra yayımlanır; diğer armatörler şirket adınızı görmez.',
    order: 'Teslim alınan sipariş',
    rewrite: '{{po}} (retten sonra yeniden)',
    ratingLabel: 'Puan',
    comment: 'Yorum',
    commentHint: '10 ile 2000 karakter. Bu siparişle ilgili olgular; kişisel veri yok.',
    submit: 'Onaya gönder',
    submitted: 'Yorum onaya gönderildi.',
    noOrders: 'Yorum yazılacak teslim alınmış sipariş yok.',
    empty: 'Henüz yayımlanmış yorum yok.',
  },
  reviewErrors: {
    INVALID_RATING: '1 ile 5 arasında bir puan seçin.',
    TOO_SHORT: 'En az 10 karakter yazın.',
    ORDER_NOT_RECEIVED: 'Yalnız teslim alınmış sipariş değerlendirilebilir.',
    REVIEW_EXISTS: 'Bu siparişin zaten bir yorumu var.',
    SUPPLIER_NOT_ON_PORTAL: 'Bu tedarikçi portalda değil.',
    INVALID_REVIEW: 'Puanı ve yorumu kontrol edin.',
  },
  readOnly: 'Tedarikçi Tedarikçi Portalı’nda girer; burada salt okunur.',
  updated: 'Tedarikçinin son güncellemesi {{date}}',
  noPortal: 'Bu tedarikçi Tedarikçi Portalı’na bağlı değil.',
  noDetails: 'Tedarikçi henüz şirket bilgisi girmedi.',
  legal: {
    title: 'Yasal ve vergi',
    legalName: 'Ticari ünvan',
    taxId: 'Vergi no',
    taxOffice: 'Vergi dairesi',
    taxCountry: 'Vergi ülkesi',
    account: 'Portal hesabı',
  },
  billing: 'Fatura adresi',
  shipping: 'Sevk adresi',
  sameAsBilling: 'Fatura adresiyle aynı',
  contacts: {
    title: 'Yetkililer',
    name: 'Ad soyad',
    role: 'Görev',
    email: 'E-posta',
    phone: 'Telefon',
  },
  bank: {
    prospect: 'Banka hesapları tedarikçi tedarikçilerinize eklendikten sonra görünür.',
    empty: 'Tedarikçi henüz banka hesabı girmedi.',
    default: 'Varsayılan',
    holder: 'Hesap sahibi',
    swift: 'SWIFT / BIC',
    show: 'Tam numarayı göster',
    hide: 'Gizle',
    audited: 'Tam numaranın görüntülenmesi denetim kaydına yazılır.',
    effective: 'Ödemede geçerli: {{bank}} {{number}}',
    noneEffective: 'Henüz onaylı hesap yok: bu hesaba ödeme yapmayın.',
    sameEffective: 'Ödemede geçerli.',
    status: {
      pending: 'Doğrulama bekliyor',
      verified: 'Doğrulandı · onay bekliyor',
      approved: 'Onaylandı',
      rejected: 'Reddedildi',
      removed: 'Tedarikçi kaldırdı',
    },
    verify: 'Doğrulama aramasını kaydet',
    approve: 'Onayla',
    reject: 'Reddet',
    awaitingOther: 'Bu değişikliği siz doğruladınız; başka bir çalışma arkadaşınız onaylar.',
    history: 'Geçmiş',
    change: {
      added: 'Tedarikçi ekledi (sürüm {{n}})',
      changed: 'Tedarikçi değiştirdi (sürüm {{n}})',
      default: 'Tedarikçi varsayılanı değiştirdi (sürüm {{n}})',
      removed: 'Tedarikçi kaldırdı (sürüm {{n}})',
    },
    review: {
      verified: '{{name}} doğruladı',
      approved: '{{name}} onayladı',
      rejected: '{{name}} reddetti',
    },
  },
  dialogs: {
    verifyTitle: 'Doğrulama aramasını kaydet',
    verifyBody:
      'Tedarikçiyi elinizde zaten olan bir numaradan arayın (bu değişiklikle verilen numaradan değil); hesap sahibini, bankayı ve tam numarayı teyit edin, kiminle konuştuğunuzu yazın. Sonra başka bir çalışma arkadaşınız onaylar.',
    verifyNote: 'Arama kaydı (aranan numara, kişi, neyin teyit edildiği)',
    verifyConfirm: 'Doğrulamayı kaydet',
    approveTitle: 'Banka hesabı onaylansın mı?',
    approveBody:
      'Onayla bu hesap şirketinizde bu tedarikçiye yapılan ödemelerde geçerli olur. Doğrulama aramasından sonraki ikinci kişi sizsiniz.',
    approveConfirm: 'Banka hesabını onayla',
    rejectTitle: 'Banka hesabı reddedilsin mi?',
    rejectBody:
      'Varsa önceki onaylı hesap geçerli kalır. Tedarikçi hesabı reddedilmiş görür ve yeniden girmelidir.',
    rejectNote: 'Gerekçe',
    rejectConfirm: 'Banka hesabını reddet',
  },
  notices: {
    verified: 'Doğrulama kaydedildi. Başka bir çalışma arkadaşınız onaylar.',
    approved: 'Banka hesabı onaylandı.',
    rejected: 'Banka hesabı reddedildi.',
  },
  bell: {
    title: 'Banka hesabı değişiklikleri',
    item: 'Banka bilgisi değişti',
    awaitingOther: 'Başka bir onaylayıcı bekleniyor',
  },
  errors: {
    NOTE_REQUIRED: 'Arama kaydını (en az 10 karakter) ya da gerekçeyi yazın.',
    NOTE_TOO_LONG: 'Not çok uzun (en çok 1000 karakter).',
    SAME_APPROVER_FORBIDDEN:
      'Doğrulama aramasını siz kaydettiniz. Bu hesabı başka bir çalışma arkadaşınız onaylar.',
    STALE_VERSION: 'Tedarikçi bu hesabı bu arada değiştirdi. Profili yeniden açın.',
    CONTENT_CHANGED: 'Tedarikçi bu hesabı bu arada değiştirdi. Profili yeniden açın.',
    INVALID_TRANSITION: 'Bu hesap zaten incelendi. Profili yeniden açın.',
    FORBIDDEN: 'Banka hesabı onayı satın alma onay yetkisi ister.',
  },
};
